import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { useThemeMode } from '@rbx/settings';
import { useTranslation } from '@rbx/intl';
import throwError from '@modules/utilities/error';
import { Alert, AlertTitle } from '@rbx/ui';

interface MermaidProps {
  code: string;
  id?: string;
}

// NOTE (@tchu, 2025-10-02): Mermaid is a component that renders a mermaid diagram.
// Usage docs: https://mermaid.js.org/syntax/flowchart.html
const Mermaid: React.FC<MermaidProps> = ({ code, id }) => {
  const { translate } = useTranslation();
  const elementRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  // Generate a random ID for the mermaid diagram like "mermaid-gd3vixrmk"
  const mermaidId = id || `mermaid-${Math.random().toString(36).substring(2, 11)}`;
  const { themeMode } = useThemeMode();
  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: themeMode === 'dark' ? 'dark' : 'default',
      securityLevel: 'loose',
      fontFamily: 'inherit',
    });
  }, [themeMode]);

  useEffect(() => {
    const renderDiagram = async () => {
      if (elementRef.current && code) {
        try {
          // Clear previous content and error
          elementRef.current.innerHTML = '';
          setError(null);

          // Render the mermaid diagram
          const { svg } = await mermaid.render(mermaidId, code);
          // Security: SVG is generated by the trusted mermaid library, not from raw user input.
          // The user's markdown code is parsed and validated by mermaid before SVG generation.
          elementRef.current.innerHTML = svg;
        } catch (err) {
          if (err instanceof Error) {
            throwError(`Mermaid error: ${err.message}`);
          }
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    };

    renderDiagram();
  }, [code, mermaidId, translate]);

  // NOTE (@tchu, 2025-10-02): Errors should only happen if content authors
  // use invalid mermaid syntax. For English, this should be a very rare occurrence
  // because they use the UI to create the diagram.
  if (error) {
    return (
      <Alert severity='error' style={{ margin: '16px 0' }}>
        <AlertTitle>
          {translate('Label.DiagramErrorMessage') || 'Failed to render diagram'}
        </AlertTitle>
        <pre style={{ marginTop: '8px', marginBottom: '0px', whiteSpace: 'pre-wrap' }}>{error}</pre>
      </Alert>
    );
  }

  return (
    <div
      ref={elementRef}
      className='mermaid-diagram'
      style={{
        textAlign: 'center',
        margin: '16px 0',
        overflow: 'auto',
      }}
    />
  );
};

export default Mermaid;
